import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Settings, Bookmark, Bell, CreditCard, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

// ============================================
// PROFILE PAGE - La tecnologia Style
// ============================================
const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  const tabs = [
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'saved', label: 'Guardados', icon: Bookmark },
    { id: 'alerts', label: 'Alertas', icon: Bell },
    // HIDDEN: Subscription tab - uncomment to restore
    // { id: 'subscription', label: 'Suscripción', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-[#1e3a5f] rounded-full flex items-center justify-center">
              <span className="text-3xl font-bold">{user.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: SERIF_FONT }}>
                {user.name}
              </h1>
              <p className="text-gray-400">{user.email}</p>
              {/* HIDDEN: Subscription badge - uncomment to restore
              <Badge className="mt-2 bg-[#1e3a5f]">
                {user.subscription_plan === 'premium' ? 'Premium' : 'Plan Gratuito'}
              </Badge>
              */}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'text-gray-900 border-red-600'
                      : 'text-gray-500 border-transparent hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
              Información Personal
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                <Input value={user.name} readOnly className="bg-gray-50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <Input value={user.email} readOnly className="bg-gray-50" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de cuenta</label>
                <Input value={user.user_type || 'registered'} readOnly className="bg-gray-50" />
              </div>
              <div className="pt-4 border-t border-gray-100">
                <Button
                  onClick={() => { logout(); navigate('/'); }}
                  variant="outline"
                  className="text-[#1e3a5f] border-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Saved Tab */}
        {activeTab === 'saved' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
              Artículos Guardados
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <Bookmark className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No tienes artículos guardados</p>
              <Link to="/dossiers">
                <Button className="mt-4 bg-[#1e3a5f] hover:bg-red-700">
                  Explorar Noticias
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
              Configuración de Alertas
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <p className="text-gray-500 mb-6">Recibe notificaciones sobre los temas que te interesan</p>
              <div className="space-y-4">
                {['Política', 'Economía', 'Negocios', 'Índice de Influencia'].map((topic) => (
                  <label key={topic} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <span className="font-medium text-gray-900">{topic}</span>
                    <input type="checkbox" className="w-5 h-5 text-[#1e3a5f] rounded" />
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
              Mi Suscripción
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-gray-500">Plan actual</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {user.subscription_plan === 'premium' ? 'Premium' : 'Gratuito'}
                  </p>
                </div>
                {user.subscription_plan !== 'premium' && (
                  <Link to="/subscription">
                    <Button className="bg-[#1e3a5f] hover:bg-red-700">
                      Actualizar a Premium
                    </Button>
                  </Link>
                )}
              </div>
              {user.subscription_plan === 'premium' ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700">Tienes acceso completo a todo el contenido premium</p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-600">Con Premium accedes a análisis exclusivos, ediciones completas y sin anuncios</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
